import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Phone, FileText, LogOut, RotateCcw, Trash2 } from "lucide-react";
import { TandaiKeluarDialog, HapusPermanenDialog } from "../components/KeluarDialogs";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BayarBadge, BayarInfo } from "../components/BayarDialogs";
import { Money, LockedTag, EmptyState, KehadiranBadge, PeralatanBadge, KategoriBadge } from "../components/common";
import { AksesPortalCard } from "../components/AksesPortalCard";
import { AnakDialog } from "./Anak";
import { api, errMsg } from "../lib/api";
import { fmtDate, fmtDateTime, num, rp } from "../lib/format";

const Stat = ({ label, children, tone = "text-gray-900", testid }) => (
  <div data-testid={testid} className="rounded-xl border border-gray-200 bg-white p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`mt-1.5 font-mono text-lg font-semibold sm:text-xl ${tone}`}>{children}</p>
  </div>
);

const ListRow = ({ left, sub, right, locked, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 last:border-0">
    <div className="min-w-0"><p className="break-words font-medium text-gray-900">{left}</p><p className="break-words text-[13px] text-gray-500">{sub}</p></div>
    <div className="flex shrink-0 items-center gap-2">{locked && <LockedTag />}{right}</div>
  </div>
);

export default function AnakDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [perusahaan, setPerusahaan] = useState([]);
  const [edit, setEdit] = useState(false);
  const [dlg, setDlg] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => api.get(`/anak/${id}/riwayat`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); api.get("/perusahaan").then((r) => setPerusahaan(r.data)); }, [load]);

  const restore = async () => {
    try { await api.post(`/anak/${id}/aktifkan`); toast.success("Anak diaktifkan kembali"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <p className="text-sm text-gray-500">Memuat…</p>;
  const a = d.anak;
  return (
    <div>
      <Link to="/anak" data-testid="anak-detail-back" className="mb-4 inline-flex h-10 items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-900"><ArrowLeft className="h-4 w-4" />Anak-anak</Link>
      <div className="fade-up mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary font-heading text-lg font-semibold text-white">{a.nama.slice(0, 2).toUpperCase()}</div>
          <div>
            <h1 data-testid="anak-detail-name" className="flex flex-wrap items-center gap-3 font-heading text-2xl font-bold tracking-tight text-gray-900">{a.nama}<KategoriBadge kategori={a.kategori} testid="anak-detail-kategori" /></h1>
            <p className="flex flex-wrap items-center gap-x-3 text-sm text-gray-500">
              <span>{a.perusahaan_nama || "Tanpa perusahaan"}</span>
              {a.no_hp && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{a.no_hp}</span>}
              <span className={a.aktif ? "font-semibold text-emerald-700" : "font-semibold text-gray-400"}>{a.aktif ? "Aktif" : "Nonaktif"}</span>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {a.keluar ? (
            <>
              <Button data-testid="anak-detail-restore" variant="outline" className="h-10 rounded-full text-primary" onClick={restore}><RotateCcw className="mr-1.5 h-4 w-4" />Aktifkan Kembali</Button>
              <Button data-testid="anak-detail-hapus" variant="outline" className="h-10 rounded-full text-red-700 hover:bg-red-50" onClick={() => setDlg("hapus")}><Trash2 className="mr-1.5 h-4 w-4" />Hapus Permanen</Button>
            </>
          ) : (
            <>
              <Button data-testid="anak-detail-edit" variant="outline" className="h-10 rounded-full" onClick={() => setEdit(true)}><Pencil className="mr-1.5 h-4 w-4" />Ubah data</Button>
              <Button data-testid="anak-detail-keluar" variant="outline" className="h-10 rounded-full text-amber-700 hover:bg-amber-50" onClick={() => setDlg("keluar")}><LogOut className="mr-1.5 h-4 w-4" />Tandai Keluar</Button>
            </>
          )}
        </div>
      </div>
      {a.keluar && (
        <div data-testid="anak-detail-keluar-banner" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <b>Sudah keluar</b> sejak {fmtDate(a.tanggal_keluar)}{a.alasan_keluar ? ` — ${a.alasan_keluar}` : ""}. Riwayat tetap bisa dilihat. Login portal diblokir.
        </div>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Kasbon outstanding" tone="text-amber-700" testid="anak-kasbon-outstanding">{rp(d.kasbon_outstanding)}</Stat>
        <Stat label="Total voucher" testid="anak-total-voucher">{num(d.total_voucher)}</Stat>
        <Stat label="Total kasbon" testid="anak-total-kasbon">{rp(d.total_kasbon)}</Stat>
        <Stat label="Sudah dipotong" tone="text-red-600" testid="anak-total-potongan">{rp(d.total_potongan)}</Stat>
      </div>

      {!a.keluar && <AksesPortalCard anak={a} onSaved={load} />}

      <Tabs defaultValue="gajian">
        <TabsList className="mb-4 h-10 w-full justify-start overflow-x-auto sm:w-auto">
          <TabsTrigger value="gajian" className="h-10 px-4" data-testid="tab-gajian">Riwayat gajian</TabsTrigger>
          <TabsTrigger value="voucher" className="h-10 px-4" data-testid="tab-voucher">Voucher ({d.vouchers.length})</TabsTrigger>
          <TabsTrigger value="kasbon" className="h-10 px-4" data-testid="tab-kasbon">Kasbon ({d.kasbon.length})</TabsTrigger>
          <TabsTrigger value="kehadiran" className="h-10 px-4" data-testid="tab-kehadiran">Kehadiran ({d.kehadiran.length})</TabsTrigger>
          <TabsTrigger value="peralatan" className="h-10 px-4" data-testid="tab-peralatan">Peralatan ({d.peralatan.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="peralatan">
          {d.peralatan.length === 0 ? <EmptyState title="Belum ada peminjaman peralatan" /> : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {d.peralatan.map((p) => (
                <ListRow key={p.id} testid={`anak-peralatan-${p.id}`} left={`${p.nama_barang}${p.jumlah > 1 ? ` ×${p.jumlah}` : ""}`}
                  sub={`Pinjam ${fmtDate(p.tanggal_pinjam)}${p.tanggal_kembali ? ` · kembali ${fmtDate(p.tanggal_kembali)}` : ""}${p.kondisi ? ` · ${p.kondisi}` : ""}${p.biaya ? ` · biaya ${rp(p.biaya)}` : ""}`}
                  right={<PeralatanBadge status={p.status_tampil} />} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="kehadiran">
          {d.kehadiran.length === 0 ? <EmptyState title="Belum ada kehadiran" /> : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {d.kehadiran.map((k) => (
                <ListRow key={k.id} testid={`anak-kehadiran-${k.id}`} left={`${fmtDate(k.tanggal)} · ${k.jam} WIB`} sub={`Wajah ${k.face_distance ?? "tidak terdeteksi"}`}
                  right={<KehadiranBadge status={k.status} />} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="gajian">
          {d.payrolls.length === 0 ? <EmptyState icon={FileText} title="Belum ada gajian yang dibayar" /> : (
            <div className="space-y-3">
              {d.payrolls.map((p) => (
                <div key={p.period} data-testid={`anak-payroll-${p.period}`} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div><p className="flex flex-wrap items-center gap-2 font-heading font-bold">{p.label}<BayarBadge bayar={p.bayar} testid={`anak-payroll-status-${p.period}`} /></p><p className="text-xs text-gray-500">{p.lunas ? `Periode lunas ${fmtDateTime(p.paid_at)}` : "Periode belum lunas"}</p></div>
                    <Button asChild variant="outline" className="h-10"><Link to={`/slip/${p.period}/${a.id}`} data-testid={`anak-slip-${p.period}`}><FileText className="mr-1.5 h-4 w-4" />Slip</Link></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
                    <Mini label="Voucher" v={num(p.voucher)} />
                    <Mini label="Kotor" v={rp(p.gaji_kotor)} />
                    <Mini label="Potongan" v={rp(p.potongan)} tone="text-red-600" />
                    <Mini label="Bersih" v={rp(p.gaji_bersih)} tone="text-primary font-semibold" />
                    <Mini label="Sisa kasbon" v={rp(p.sisa_kasbon)} tone="text-amber-700" />
                  </div>
                  <details className="mt-3 border-t border-gray-100 pt-3" data-testid={`anak-payroll-detail-${p.period}`}>
                    <summary className="cursor-pointer text-sm font-semibold text-primary">Detail pembayaran</summary>
                    <div className="mt-3"><BayarInfo bayar={p.bayar} buktiSrc={`/payroll/${p.period}/bayar/${a.id}/bukti`} prefix={`anak-bayar-${p.period}`} /></div>
                  </details>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="voucher">
          {d.vouchers.length === 0 ? <EmptyState title="Belum ada voucher" /> : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {d.vouchers.map((v) => (
                <ListRow key={v.id} testid={`anak-voucher-${v.id}`} left={`${v.tamu} voucher · ${fmtDate(v.tanggal)}`} sub={v.perusahaan_nama || "-"} locked={v.locked}
                  right={<Money value={v.tamu * d.tarif} className="text-sm" />} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="kasbon">
          {d.kasbon.length === 0 ? <EmptyState title="Belum ada kasbon" /> : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {d.kasbon.map((k) => (
                <ListRow key={k.id} testid={`anak-kasbon-${k.id}`} left={fmtDate(k.tanggal)} sub={k.keterangan || "-"} locked={k.locked}
                  right={<Money value={k.jumlah} className="text-sm text-amber-700" />} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      <TandaiKeluarDialog anak={a} open={dlg === "keluar"} onOpenChange={(o) => !o && setDlg("")} onDone={load} />
      <HapusPermanenDialog anak={a} open={dlg === "hapus"} onOpenChange={(o) => !o && setDlg("")} onDone={() => navigate("/anak")} />
      <AnakDialog open={edit} onOpenChange={setEdit} initial={a} perusahaan={perusahaan} onSaved={load} />
    </div>
  );
}

const Mini = ({ label, v, tone = "text-gray-900" }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-gray-400">{label}</p><p className={`font-mono ${tone}`}>{v}</p></div>
);
